import { useCallback, useMemo, useRef, useState } from 'react';
import type {
  DrillSpec,
  DrillEntity,
  DrillCone,
  DrillArrow,
  DrillZone,
  DrillText,
  DrillKeyframe,
  NormPoint,
} from '@4cm/core/drills/types';
import type { EntityType } from '@4cm/core/videoImport/types';

type ConeVariant = NonNullable<DrillCone['variant']>;
type EditorMode = 'select' | 'arrow' | 'zone' | 'text';
import {
  DEFAULT_VIEW_WIDTH,
  geometryFor,
  nToPx,
  pxToN,
  extractDrillSpec,
} from './DrillCanvasPreview';
import './DrillCanvasPreview.css';
import './DrillCanvasEditor.css';

interface Props {
  /** The spec being edited. Editor calls onChange whenever the coach mutates
   *  it (drag a token, add an entity, delete one). Render flows downward —
   *  the parent owns the spec; this component is fully controlled. */
  spec: DrillSpec;
  onChange: (next: DrillSpec) => void;
  /** Visual width budget in pixels. Height derives from pitch aspect. */
  width?: number;
}

// -----------------------------------------------------------------------------
// Stable id generation. Crypto.randomUUID() works on all modern browsers we
// target; falls back to Math.random for environments that don't have it.
// -----------------------------------------------------------------------------

function newId(prefix: string): string {
  const c = (globalThis as unknown as { crypto?: { randomUUID?: () => string } }).crypto;
  const uuid = c?.randomUUID ? c.randomUUID() : null;
  return uuid
    ? `${prefix}-${uuid.slice(0, 8)}`
    : `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

// -----------------------------------------------------------------------------
// Entity rendering — interactive variant. Same visual contract as
// DrillCanvasPreview's EntityNode, but wraps each token in a `<g>` with
// pointer handlers and a selection ring.
// -----------------------------------------------------------------------------

function entityFill(entity: DrillEntity): string {
  if (entity.color) return entity.color;
  if (entity.type === 'ball') return '#FFFFFF';
  if (entity.type === 'home') return '#2980B9';
  if (entity.type === 'away') return '#C0392B';
  if (entity.type === 'official') return '#7F8C8D';
  return '#95A5A6';
}

interface EditableEntityProps {
  entity: DrillEntity;
  geom: ReturnType<typeof geometryFor>;
  selected: boolean;
  /** Visual ring colour. Undefined = default selection style; passed in
   *  so arrow-mode source highlights as cyan, regular selection as
   *  purple. */
  ringColor?: string;
  /** When the editor is in 'arrow' mode, click handlers replace drag —
   *  no token movement, just pick. */
  mode: EditorMode;
  onStartDrag: (entityId: string, e: React.PointerEvent<SVGGElement>) => void;
  onSelect: (entityId: string) => void;
}

function EditableEntity({ entity, geom, selected, ringColor, mode, onStartDrag, onSelect }: EditableEntityProps) {
  const { x, y } = nToPx(entity.position, geom);
  const yd = geom.pxPerYd;
  const isBall = entity.type === 'ball';
  const r = isBall ? 0.6 * yd : 1.2 * yd;
  const ringStroke = ringColor ?? 'rgba(134, 59, 255, 0.95)';
  return (
    <g
      onPointerDown={(e) => {
        onSelect(entity.id);
        if (mode === 'select') onStartDrag(entity.id, e);
      }}
      style={{ cursor: mode === 'arrow' ? 'crosshair' : 'grab', touchAction: 'none' }}
    >
      {selected && (
        <circle
          cx={x}
          cy={y}
          r={r + 0.6 * yd}
          fill="none"
          stroke={ringStroke}
          strokeWidth={0.25 * yd}
          strokeDasharray={`${0.4 * yd},${0.3 * yd}`}
        />
      )}
      <circle
        cx={x}
        cy={y}
        r={r}
        fill={entityFill(entity)}
        stroke="rgba(0,0,0,0.45)"
        strokeWidth={0.15 * yd}
      />
      {!isBall && entity.label && (
        <text
          x={x}
          y={y + 0.4 * yd}
          textAnchor="middle"
          fill="white"
          fontSize={1.1 * yd}
          fontWeight={600}
          pointerEvents="none"
        >
          {entity.label}
        </text>
      )}
    </g>
  );
}

// -----------------------------------------------------------------------------
// Cone rendering — read-only here. Editor only mutates entities + cones via
// the tray; full cone editing (variant swap, rotation) is a follow-up sub-pass.
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// Arrows — click-to-select, click background to deselect.
// -----------------------------------------------------------------------------

interface EditableArrowProps {
  arrow: DrillArrow;
  entityById: Map<string, DrillEntity>;
  geom: ReturnType<typeof geometryFor>;
  selected: boolean;
  onSelect: (id: string) => void;
}

function EditableArrow({ arrow, entityById, geom, selected, onSelect }: EditableArrowProps) {
  const yd = geom.pxPerYd;
  const resolve = (anchor: DrillArrow['from']) => {
    if (anchor.kind === 'entity') {
      const e = entityById.get(anchor.entityId);
      return e ? nToPx(e.position, geom) : null;
    }
    return nToPx(anchor.position, geom);
  };
  const from = resolve(arrow.from);
  const to = resolve(arrow.to);
  if (!from || !to) return null;

  const stroke = selected
    ? 'rgba(134, 59, 255, 0.95)'
    : (arrow.color ?? '#3498DB');

  return (
    <g
      onPointerDown={(e) => {
        e.stopPropagation();
        onSelect(arrow.id);
      }}
      style={{ cursor: 'pointer' }}
    >
      {/* Wide invisible hit area to make the line easy to tap on touch. */}
      <line
        x1={from.x}
        y1={from.y}
        x2={to.x}
        y2={to.y}
        stroke="transparent"
        strokeWidth={1.5 * yd}
        strokeLinecap="round"
      />
      <line
        x1={from.x}
        y1={from.y}
        x2={to.x}
        y2={to.y}
        stroke={stroke}
        strokeWidth={0.4 * yd}
        strokeLinecap="round"
        strokeDasharray={selected ? `${0.4 * yd},${0.3 * yd}` : undefined}
      />
      {/* Arrowhead */}
      {(() => {
        const dx = to.x - from.x;
        const dy = to.y - from.y;
        const len = Math.hypot(dx, dy) || 1;
        const ux = dx / len;
        const uy = dy / len;
        const headLen = 1.2 * yd;
        const headWid = 0.6 * yd;
        const baseX = to.x - ux * headLen;
        const baseY = to.y - uy * headLen;
        const px = -uy;
        const py = ux;
        return (
          <polygon
            points={
              `${to.x},${to.y} ` +
              `${baseX + px * headWid},${baseY + py * headWid} ` +
              `${baseX - px * headWid},${baseY - py * headWid}`
            }
            fill={stroke}
          />
        );
      })()}
    </g>
  );
}

function StaticZone({ zone, geom, selected, onSelect }: {
  zone: DrillZone;
  geom: ReturnType<typeof geometryFor>;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  const yd = geom.pxPerYd;
  const stroke = selected ? 'rgba(134, 59, 255, 0.95)' : (zone.stroke ?? 'rgba(192, 57, 43, 0.55)');
  const fill = zone.fill ?? 'rgba(192, 57, 43, 0.15)';
  const sw = 0.3 * yd;
  const onPointerDown = (e: React.PointerEvent<SVGElement>) => {
    e.stopPropagation();
    onSelect(zone.id);
  };
  if (zone.shape.kind === 'rect') {
    const tl = nToPx(zone.shape.topLeft, geom);
    const br = nToPx(zone.shape.bottomRight, geom);
    return (
      <rect
        x={Math.min(tl.x, br.x)}
        y={Math.min(tl.y, br.y)}
        width={Math.abs(br.x - tl.x)}
        height={Math.abs(br.y - tl.y)}
        fill={fill}
        stroke={stroke}
        strokeWidth={sw}
        strokeDasharray={selected ? `${0.5 * yd},${0.4 * yd}` : undefined}
        onPointerDown={onPointerDown}
        style={{ cursor: 'pointer' }}
      />
    );
  }
  // circle
  const c = nToPx(zone.shape.center, geom);
  return (
    <circle
      cx={c.x}
      cy={c.y}
      r={zone.shape.radiusNorm * geom.pitchYdW * geom.pxPerYd}
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeDasharray={selected ? `${0.5 * yd},${0.4 * yd}` : undefined}
      onPointerDown={onPointerDown}
      style={{ cursor: 'pointer' }}
    />
  );
}

function StaticText({ text, geom, selected, onSelect }: {
  text: DrillText;
  geom: ReturnType<typeof geometryFor>;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  const { x, y } = nToPx(text.position, geom);
  const fontSize = (text.fontSize ?? 1.4) * geom.pxPerYd;
  const onPointerDown = (e: React.PointerEvent<SVGTextElement>) => {
    e.stopPropagation();
    onSelect(text.id);
  };
  return (
    <text
      x={x}
      y={y}
      fill={selected ? 'rgba(134, 59, 255, 0.95)' : (text.color ?? 'rgba(255,255,255,0.9)')}
      fontSize={fontSize}
      textAnchor="middle"
      onPointerDown={onPointerDown}
      style={{ cursor: 'pointer' }}
    >
      {text.content}
    </text>
  );
}

function StaticCone({ cone, geom }: { cone: DrillCone; geom: ReturnType<typeof geometryFor> }) {
  const { x, y } = nToPx(cone.position, geom);
  const yd = geom.pxPerYd;
  const variant = cone.variant ?? 'cone';
  const fill = cone.color ?? '#F39C12';
  if (variant === 'flat') {
    return <circle cx={x} cy={y} r={0.5 * yd} fill={fill} opacity={0.85} />;
  }
  return (
    <polygon
      points={`${x},${y - 0.7 * yd} ${x - 0.6 * yd},${y + 0.5 * yd} ${x + 0.6 * yd},${y + 0.5 * yd}`}
      fill={fill}
    />
  );
}

// -----------------------------------------------------------------------------
// Pitch lines (copied from preview to keep editor self-contained).
// -----------------------------------------------------------------------------

function PitchPaint({ geom }: { geom: ReturnType<typeof geometryFor> }) {
  const yd = geom.pxPerYd;
  const w = geom.viewW;
  const h = geom.viewH;
  return (
    <g>
      <rect
        x={0}
        y={0}
        width={w}
        height={h}
        rx={2 * yd}
        fill="rgba(36, 76, 50, 0.78)"
        stroke="rgba(255, 255, 255, 0.4)"
        strokeWidth={0.4 * yd}
      />
      <line
        x1={0}
        y1={h / 2}
        x2={w}
        y2={h / 2}
        stroke="rgba(255, 255, 255, 0.4)"
        strokeWidth={0.3 * yd}
      />
      <circle
        cx={w / 2}
        cy={h / 2}
        r={10 * yd}
        fill="none"
        stroke="rgba(255, 255, 255, 0.4)"
        strokeWidth={0.3 * yd}
      />
      <circle cx={w / 2} cy={h / 2} r={0.5 * yd} fill="rgba(255, 255, 255, 0.6)" />
    </g>
  );
}

// -----------------------------------------------------------------------------
// Editor component
// -----------------------------------------------------------------------------

const ENTITY_DEFAULTS: Record<EntityType, { color: string; prefix: string }> = {
  home: { color: '#2980B9', prefix: 'H' },
  away: { color: '#C0392B', prefix: 'A' },
  ball: { color: '#FFFFFF', prefix: 'B' },
  official: { color: '#7F8C8D', prefix: 'R' },
};

const CONE_VARIANTS: ConeVariant[] = ['cone', 'flat'];

/**
 * Interactive drill editor. The minimum viable editor — drag entities to
 * reposition, add/remove entities, add/remove cones. Coaches use this to
 * fine-tune what Brain drafted before saving.
 *
 * Out of scope (deferred sub-pass): arrow drawing, zone editing, text
 * placement, animation keyframe authoring, undo/redo. These exist in
 * apps/web's DrillCanvas (~733 LOC) but doubling that for v1 would push
 * this surface past its useful weight. Coach can refine textually via
 * Brain when they need those affordances.
 */
export function DrillCanvasEditor({ spec, onChange, width = DEFAULT_VIEW_WIDTH }: Props) {
  const geom = useMemo(() => geometryFor(spec, width), [spec, width]);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedArrowId, setSelectedArrowId] = useState<string | null>(null);
  const dragRef = useRef<{ entityId: string; pointerId: number } | null>(null);

  // Editor mode — 'select' is the default (drag tokens, edit them);
  // 'arrow' mode replaces drag with two-click arrow drawing;
  // 'zone' mode lets the coach drag a rectangle on the pitch;
  // 'text' mode places a text label on the next click.
  const [mode, setMode] = useState<EditorMode>('select');
  const [arrowSourceId, setArrowSourceId] = useState<string | null>(null);
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [selectedTextId, setSelectedTextId] = useState<string | null>(null);
  // In-flight zone draw — set on pointer-down in 'zone' mode, updated
  // on pointer-move, finalised on pointer-up.
  const [zoneDraft, setZoneDraft] = useState<{ from: NormPoint; to: NormPoint } | null>(null);
  // Quick lookup map for arrow renderer.
  const entityById = useMemo(
    () => new Map(spec.entities.map((e) => [e.id, e])),
    [spec.entities],
  );

  // Convert a pointer event's clientX/Y into normalised pitch coordinates,
  // accounting for SVG viewBox scaling (the SVG is 100% width, the viewBox
  // is `0 0 viewW viewH`, so clientX-bbox.left maps proportionally).
  const eventToNorm = useCallback((e: React.PointerEvent | PointerEvent) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const bbox = svg.getBoundingClientRect();
    if (bbox.width === 0 || bbox.height === 0) return null;
    const xPx = ((e.clientX - bbox.left) / bbox.width) * geom.viewW;
    const yPx = ((e.clientY - bbox.top) / bbox.height) * geom.viewH;
    return pxToN({ x: xPx, y: yPx }, geom);
  }, [geom]);

  const handleStartDrag = useCallback((entityId: string, e: React.PointerEvent<SVGGElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { entityId, pointerId: e.pointerId };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    // Zone-draw: extend the in-flight rectangle to the current pointer.
    if (mode === 'zone' && zoneDraft) {
      const norm = eventToNorm(e);
      if (!norm) return;
      setZoneDraft({ from: zoneDraft.from, to: norm });
      return;
    }
    // Token-drag (select mode).
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const norm = eventToNorm(e);
    if (!norm) return;
    onChange({
      ...spec,
      entities: spec.entities.map((ent) =>
        ent.id === drag.entityId ? { ...ent, position: norm } : ent,
      ),
    });
  }, [eventToNorm, onChange, spec, mode, zoneDraft]);

  const handlePointerUp = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    // Finalise zone draw when we have a draft + non-trivial size (>2%
    // pitch on each axis — anything smaller is a misclick).
    if (mode === 'zone' && zoneDraft) {
      const dx = Math.abs(zoneDraft.to[0] - zoneDraft.from[0]);
      const dy = Math.abs(zoneDraft.to[1] - zoneDraft.from[1]);
      if (dx > 0.02 && dy > 0.02) {
        const newZone: DrillZone = {
          id: newId('zone'),
          shape: { kind: 'rect', topLeft: zoneDraft.from, bottomRight: zoneDraft.to },
          stroke: 'rgba(192, 57, 43, 0.55)',
          fill: 'rgba(192, 57, 43, 0.15)',
        };
        onChange({ ...spec, zones: [...(spec.zones ?? []), newZone] });
      }
      setZoneDraft(null);
      setMode('select');
    }
    if (dragRef.current?.pointerId === e.pointerId) {
      dragRef.current = null;
    }
  }, [mode, zoneDraft, onChange, spec]);

  // Pointer-down on the SVG background. Differs by mode:
  //   - select: clear all selections.
  //   - arrow:  clear the in-flight arrow source.
  //   - zone:   start a zone-draft rectangle from this point.
  //   - text:   place a text label here, prompt for content.
  const handleBackgroundPointerDown = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    setSelectedId(null);
    setSelectedArrowId(null);
    setSelectedZoneId(null);
    setSelectedTextId(null);
    if (mode === 'arrow') {
      setArrowSourceId(null);
      return;
    }
    if (mode === 'zone') {
      const norm = eventToNorm(e);
      if (!norm) return;
      setZoneDraft({ from: norm, to: norm });
      return;
    }
    if (mode === 'text') {
      const norm = eventToNorm(e);
      if (!norm) return;
      const content = window.prompt('Text label:', '');
      if (content && content.trim()) {
        const newText: DrillText = {
          id: newId('text'),
          position: norm,
          content: content.trim(),
        };
        onChange({ ...spec, texts: [...(spec.texts ?? []), newText] });
      }
      setMode('select');
    }
  }, [mode, eventToNorm, onChange, spec]);

  const handleAddEntity = useCallback((type: EntityType) => {
    const sameTypeCount = spec.entities.filter((ent) => ent.type === type).length;
    const id = newId(type);
    const next: DrillEntity = {
      id,
      type,
      position: [0.5, 0.5],
      label: type === 'ball' ? '' : `${ENTITY_DEFAULTS[type].prefix}${sameTypeCount + 1}`,
    };
    setSelectedId(id);
    onChange({ ...spec, entities: [...spec.entities, next] });
  }, [onChange, spec]);

  const handleAddCone = useCallback((variant: ConeVariant) => {
    const next: DrillCone = {
      id: newId('cone'),
      position: [0.5, 0.5],
      variant,
    };
    onChange({ ...spec, cones: [...(spec.cones ?? []), next] });
  }, [onChange, spec]);

  const handleDeleteSelected = useCallback(() => {
    if (!selectedId) return;
    onChange({
      ...spec,
      entities: spec.entities.filter((ent) => ent.id !== selectedId),
      // Drop any arrows that anchored to the removed entity — leaves the
      // spec consistent without separate arrow editing.
      arrows: (spec.arrows ?? []).filter(
        (a) =>
          (a.from.kind !== 'entity' || a.from.entityId !== selectedId) &&
          (a.to.kind !== 'entity' || a.to.entityId !== selectedId),
      ),
    });
    setSelectedId(null);
  }, [onChange, selectedId, spec]);

  const handleClearCones = useCallback(() => {
    onChange({ ...spec, cones: [] });
  }, [onChange, spec]);

  // Click-on-entity behaviour depends on mode. In 'select', selection is
  // visual + the existing drag handler kicks off (registered separately
  // by EditableEntity). In 'arrow', the click never starts a drag — it
  // either sets the source entity (first click) or completes the arrow
  // (second click, on a different entity).
  const handleEntitySelect = useCallback((entityId: string) => {
    setSelectedArrowId(null);
    if (mode !== 'arrow') {
      setSelectedId(entityId);
      return;
    }
    if (!arrowSourceId) {
      setArrowSourceId(entityId);
      return;
    }
    if (arrowSourceId === entityId) {
      // Same entity clicked twice — cancel selection rather than create
      // a self-loop (those break the renderer's resolve fn).
      setArrowSourceId(null);
      return;
    }
    const newArrow: DrillArrow = {
      id: newId('arrow'),
      from: { kind: 'entity', entityId: arrowSourceId },
      to: { kind: 'entity', entityId },
      kind: 'pass',
      style: 'solid',
    };
    onChange({ ...spec, arrows: [...(spec.arrows ?? []), newArrow] });
    setArrowSourceId(null);
    setMode('select');
  }, [mode, arrowSourceId, onChange, spec]);

  const handleDeleteSelectedArrow = useCallback(() => {
    if (!selectedArrowId) return;
    onChange({
      ...spec,
      arrows: (spec.arrows ?? []).filter((a) => a.id !== selectedArrowId),
    });
    setSelectedArrowId(null);
  }, [onChange, selectedArrowId, spec]);

  const toggleArrowMode = useCallback(() => {
    setMode((m) => (m === 'arrow' ? 'select' : 'arrow'));
    setArrowSourceId(null);
    setSelectedId(null);
    setSelectedArrowId(null);
  }, []);

  const toggleZoneMode = useCallback(() => {
    setMode((m) => (m === 'zone' ? 'select' : 'zone'));
    setZoneDraft(null);
  }, []);

  const toggleTextMode = useCallback(() => {
    setMode((m) => (m === 'text' ? 'select' : 'text'));
  }, []);

  const handleDeleteSelectedZone = useCallback(() => {
    if (!selectedZoneId) return;
    onChange({ ...spec, zones: (spec.zones ?? []).filter((z) => z.id !== selectedZoneId) });
    setSelectedZoneId(null);
  }, [onChange, selectedZoneId, spec]);

  const handleDeleteSelectedText = useCallback(() => {
    if (!selectedTextId) return;
    onChange({ ...spec, texts: (spec.texts ?? []).filter((t) => t.id !== selectedTextId) });
    setSelectedTextId(null);
  }, [onChange, selectedTextId, spec]);

  // Keyframe authoring — snapshot the current entity positions as the
  // next keyframe in the sequence. The DrillCanvasPlayer already
  // interpolates between keyframes; this just captures the targets the
  // coach has set up by dragging tokens.
  const handleSnapshotKeyframe = useCallback(() => {
    const positions: Record<string, NormPoint> = {};
    for (const e of spec.entities) {
      positions[e.id] = e.position;
    }
    const next: DrillKeyframe = {
      id: newId('kf'),
      durationSec: 2,
      positions,
    };
    onChange({ ...spec, keyframes: [...(spec.keyframes ?? []), next] });
  }, [onChange, spec]);

  const handleRemoveKeyframe = useCallback((keyframeId: string) => {
    onChange({
      ...spec,
      keyframes: (spec.keyframes ?? []).filter((k) => k.id !== keyframeId),
    });
  }, [onChange, spec]);

  const handleSetKeyframeDuration = useCallback((keyframeId: string, sec: number) => {
    onChange({
      ...spec,
      keyframes: (spec.keyframes ?? []).map((k) =>
        k.id === keyframeId ? { ...k, durationSec: Math.max(0.5, sec) } : k,
      ),
    });
  }, [onChange, spec]);

  return (
    <div className="drill-editor" data-mode={mode}>
      <div className="drill-canvas-preview" style={{ width: geom.viewW }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${geom.viewW} ${geom.viewH}`}
          width="100%"
          height="auto"
          role="img"
          aria-label="Drill diagram editor"
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onPointerDown={handleBackgroundPointerDown}
          style={{ touchAction: 'none', cursor: mode === 'zone' || mode === 'text' ? 'crosshair' : undefined }}
        >
          <PitchPaint geom={geom} />
          {/* Zones render UNDER everything else so tokens stay visible. */}
          {(spec.zones ?? []).map((z) => (
            <StaticZone
              key={z.id}
              zone={z}
              geom={geom}
              selected={z.id === selectedZoneId}
              onSelect={(id) => {
                setSelectedZoneId(id);
                setSelectedId(null);
                setSelectedArrowId(null);
                setSelectedTextId(null);
              }}
            />
          ))}
          {zoneDraft && (() => {
            const tl = nToPx(zoneDraft.from, geom);
            const br = nToPx(zoneDraft.to, geom);
            return (
              <rect
                x={Math.min(tl.x, br.x)}
                y={Math.min(tl.y, br.y)}
                width={Math.abs(br.x - tl.x)}
                height={Math.abs(br.y - tl.y)}
                fill="rgba(134, 59, 255, 0.15)"
                stroke="rgba(134, 59, 255, 0.8)"
                strokeWidth={0.3 * geom.pxPerYd}
                strokeDasharray={`${0.4 * geom.pxPerYd},${0.3 * geom.pxPerYd}`}
                pointerEvents="none"
              />
            );
          })()}
          {(spec.cones ?? []).map((c) => (
            <StaticCone key={c.id} cone={c} geom={geom} />
          ))}
          {(spec.arrows ?? []).map((a) => (
            <EditableArrow
              key={a.id}
              arrow={a}
              entityById={entityById}
              geom={geom}
              selected={a.id === selectedArrowId}
              onSelect={(id) => {
                setSelectedArrowId(id);
                setSelectedId(null);
                setSelectedZoneId(null);
                setSelectedTextId(null);
              }}
            />
          ))}
          {spec.entities.map((e) => {
            const isArrowSource = mode === 'arrow' && arrowSourceId === e.id;
            return (
              <EditableEntity
                key={e.id}
                entity={e}
                geom={geom}
                selected={e.id === selectedId || isArrowSource}
                ringColor={isArrowSource ? 'rgba(46, 204, 113, 0.95)' : undefined}
                mode={mode}
                onStartDrag={handleStartDrag}
                onSelect={handleEntitySelect}
              />
            );
          })}
          {(spec.texts ?? []).map((t) => (
            <StaticText
              key={t.id}
              text={t}
              geom={geom}
              selected={t.id === selectedTextId}
              onSelect={(id) => {
                setSelectedTextId(id);
                setSelectedId(null);
                setSelectedArrowId(null);
                setSelectedZoneId(null);
              }}
            />
          ))}
        </svg>
      </div>
      <div className="drill-editor__tray" role="toolbar" aria-label="Drill editor controls">
        <span className="drill-editor__tray-label">Add</span>
        {(['home', 'away', 'ball', 'official'] as EntityType[]).map((t) => (
          <button
            key={t}
            type="button"
            className="drill-editor__chip"
            data-kind={t}
            onClick={() => handleAddEntity(t)}
            title={`Add ${t}`}
          >
            <span className="drill-editor__chip-dot" style={{ background: ENTITY_DEFAULTS[t].color }} />
            {t}
          </button>
        ))}
        {CONE_VARIANTS.map((v) => (
          <button
            key={v}
            type="button"
            className="drill-editor__chip"
            data-kind="cone"
            onClick={() => handleAddCone(v)}
            title={`Add ${v}`}
          >
            <span className="drill-editor__chip-dot" style={{ background: '#F39C12' }} />
            {v}
          </button>
        ))}
        <button
          type="button"
          className="drill-editor__chip"
          data-active={mode === 'arrow' || undefined}
          onClick={toggleArrowMode}
          title="Click two players to draw an arrow between them"
        >
          {mode === 'arrow' ? '✕ Cancel arrow' : '↗ Arrow'}
        </button>
        <button
          type="button"
          className="drill-editor__chip"
          data-active={mode === 'zone' || undefined}
          onClick={toggleZoneMode}
          title="Drag on the pitch to draw a zone rectangle"
        >
          {mode === 'zone' ? '✕ Cancel zone' : '▭ Zone'}
        </button>
        <button
          type="button"
          className="drill-editor__chip"
          data-active={mode === 'text' || undefined}
          onClick={toggleTextMode}
          title="Click on the pitch to drop a text label"
        >
          {mode === 'text' ? '✕ Cancel text' : 'T Text'}
        </button>
        <span className="drill-editor__tray-spacer" />
        <button
          type="button"
          className="drill-editor__chip drill-editor__chip--danger"
          onClick={handleDeleteSelected}
          disabled={!selectedId}
          title="Delete selected entity"
        >
          Remove selected
        </button>
        {selectedArrowId && (
          <button
            type="button"
            className="drill-editor__chip drill-editor__chip--danger"
            onClick={handleDeleteSelectedArrow}
            title="Remove the selected arrow"
          >
            Remove arrow
          </button>
        )}
        {selectedZoneId && (
          <button
            type="button"
            className="drill-editor__chip drill-editor__chip--danger"
            onClick={handleDeleteSelectedZone}
            title="Remove the selected zone"
          >
            Remove zone
          </button>
        )}
        {selectedTextId && (
          <button
            type="button"
            className="drill-editor__chip drill-editor__chip--danger"
            onClick={handleDeleteSelectedText}
            title="Remove the selected text label"
          >
            Remove text
          </button>
        )}
        {(spec.cones ?? []).length > 0 && (
          <button
            type="button"
            className="drill-editor__chip drill-editor__chip--danger"
            onClick={handleClearCones}
            title="Remove all cones"
          >
            Clear cones
          </button>
        )}
      </div>
      <p className="drill-editor__hint">
        {mode === 'arrow'
          ? arrowSourceId
            ? 'Now click the second player — the arrow will go from the highlighted source to whoever you tap next.'
            : 'Click the player you want the arrow to start from.'
          : mode === 'zone'
            ? 'Press and drag on the pitch to draw a zone rectangle.'
            : mode === 'text'
              ? 'Click anywhere on the pitch to drop a text label — you\'ll be prompted for the content.'
              : 'Drag tokens to reposition. Tap arrows / zones / labels to select + remove. Toolbar above to draw new ones.'}
      </p>

      <div className="drill-editor__keyframes" role="toolbar" aria-label="Keyframe authoring">
        <span className="drill-editor__tray-label">Keyframes</span>
        <button
          type="button"
          className="drill-editor__chip"
          onClick={handleSnapshotKeyframe}
          title="Capture the current player positions as the next keyframe in the playback sequence"
        >
          📸 Snapshot
        </button>
        {(spec.keyframes ?? []).map((kf, idx) => (
          <div key={kf.id} className="drill-editor__keyframe-item">
            <span className="drill-editor__keyframe-num">{idx + 1}</span>
            <input
              type="number"
              className="drill-editor__keyframe-dur"
              value={kf.durationSec}
              min={0.5}
              max={20}
              step={0.5}
              onChange={(e) => handleSetKeyframeDuration(kf.id, parseFloat(e.target.value) || 0.5)}
              aria-label={`Keyframe ${idx + 1} duration in seconds`}
              title="Duration in seconds"
            />
            <button
              type="button"
              className="drill-editor__keyframe-del"
              onClick={() => handleRemoveKeyframe(kf.id)}
              aria-label={`Remove keyframe ${idx + 1}`}
            >
              ×
            </button>
          </div>
        ))}
        {(spec.keyframes ?? []).length === 0 && (
          <span className="drill-editor__keyframes-empty">
            No keyframes yet. Drag tokens, then snapshot to capture the next phase of the drill.
          </span>
        )}
      </div>
    </div>
  );
}

export { extractDrillSpec };
